import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { api } from '../../services/api';
import type { Property } from '../../types';

export const Units: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: res, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
  });
  const properties = useMemo(() => {
    const raw = res?.data;
    const data: any[] = Array.isArray(raw) ? raw : [];
    return data.map((p: any) => {
      const units = p.units || [];
      const occupiedFromUnits = units.filter((u: any) => u.status === 'occupied' || u.tenantId).length;
      const tenantsForProperty = p.tenants || [];
      const occupiedFromTenants = tenantsForProperty.filter((t: any) => t.status === 'active').length;
      const occupiedCount = Math.max(occupiedFromUnits, occupiedFromTenants, p.occupiedUnits ?? 0);
      const rawUnitCount = p.unitCount ?? units.length;
      const unitCount = Math.max(rawUnitCount, occupiedCount);
      return {
        ...p,
        unitCount,
        occupiedUnits: occupiedCount,
      };
    });
  }, [res]);
  const error = queryError ? (queryError as Error)?.message ?? 'Error al cargar unidades' : null;

  const totalUnits = properties.reduce((sum, p) => sum + (p.unitCount || 0), 0);
  const totalOccupied = properties.reduce((sum, p) => sum + (p.occupiedUnits || 0), 0);
  const totalVacant = totalUnits - totalOccupied;

  const columns: Column<Property>[] = [
    { header: 'Propiedad', accessor: 'name', sortable: true },
    { header: 'Ciudad', accessor: 'city', sortable: true },
    { header: 'Total Unidades', accessor: 'unitCount', sortable: true },
    { header: 'Ocupadas', accessor: 'occupiedUnits', sortable: true },
    {
      header: 'Vacantes',
      accessor: (row) => Math.max(0, (row.unitCount || 0) - (row.occupiedUnits || 0)),
      sortable: true,
    },
    {
      header: '% Ocupación',
      accessor: (row) => {
        const total = row.unitCount || 0;
        const occupied = row.occupiedUnits || 0;
        if (total === 0 && occupied === 0) return '-';
        const pct = total > 0 ? (occupied / total) * 100 : (occupied > 0 ? 100 : 0);
        return `${Math.round(pct)}%`;
      },
      sortable: true,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <Building2 className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Unidades</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <Building2 className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Unidades</h1>
        </div>
        <button
          type="button"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['properties', 'list'] })}
          disabled={loading}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-600 mb-1">Total Unidades</div>
          <div className="text-2xl font-bold text-primary">{totalUnits}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Ocupadas</div>
          <div className="text-2xl font-bold text-green-600">{totalOccupied}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Vacantes</div>
          <div className="text-2xl font-bold text-red-600">{totalVacant}</div>
        </Card>
      </div>

      <Card title="Detalle por Propiedad">
        {properties.length === 0 ? (
          <p className="text-gray-500 py-4">No hay propiedades con unidades.</p>
        ) : (
          <Table data={properties} columns={columns} />
        )}
      </Card>
    </div>
  );
};
